import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, Download, Search, X, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";

const toHtml = (s) => (s || "")
  .replace(/<font face='Courier' size='8'>(.*?)<\/font>/g, "<code>$1</code>")
  .replace(/<font face="Courier" size="8">(.*?)<\/font>/g, "<code>$1</code>");

const strip = (s) => (s || "").replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

function highlight(html, q) {
  if (!q) return html;
  const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${esc})`, "gi");
  return html.split(/(<[^>]+>)/g).map((part) => (part.startsWith("<") ? part : part.replace(re, "<mark>$1</mark>"))).join("");
}

function blockText(b) {
  if (b.type === "table") return b.rows.flat().map(strip).join(" ");
  if (b.items) return b.items.map(strip).join(" ");
  return strip(b.text);
}

function Block({ b, q }) {
  const H = (html) => ({ __html: highlight(toHtml(html), q) });
  if (b.type === "h2") return <h3 className="text-base md:text-lg font-semibold text-[#10B981] mt-6 mb-2" dangerouslySetInnerHTML={H(b.text)} />;
  if (b.type === "h3") return <h4 className="text-sm font-semibold text-slate-100 mt-4 mb-1" dangerouslySetInnerHTML={H(b.text)} />;
  if (b.type === "p") return <p className="text-sm text-slate-300 leading-relaxed mb-2" dangerouslySetInnerHTML={H(b.text)} />;
  if (b.type === "note") return <div className="rounded-xl border border-[#10B981]/40 bg-[#10B981]/5 p-3 text-sm text-slate-300 my-3"><b className="text-[#10B981]">Nota. </b><span dangerouslySetInnerHTML={H(b.text)} /></div>;
  if (b.type === "warn") return <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-3 text-sm text-slate-300 my-3"><b className="text-[#F59E0B]">Attenzione. </b><span dangerouslySetInnerHTML={H(b.text)} /></div>;
  if (b.type === "code") return <pre className="rounded-xl bg-[#0A0E17] border border-[#1F2937] p-3 text-[11px] text-slate-300 overflow-x-auto my-3 font-mono2 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: highlight(b.text.replace(/</g, "&lt;"), q) }} />;
  if (b.type === "bullets") return <ul className="list-disc pl-5 space-y-1 mb-3">{b.items.map((it, i) => <li key={i} className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={H(it)} />)}</ul>;
  if (b.type === "steps") return <ol className="list-decimal pl-5 space-y-1.5 mb-3">{b.items.map((it, i) => <li key={i} className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={H(it)} />)}</ol>;
  if (b.type === "table") return (
    <div className="overflow-x-auto my-3 rounded-xl border border-[#1F2937] max-w-full">
      <table className="w-full text-xs [overflow-wrap:anywhere]">
        <thead><tr className="bg-[#0B1F4B] text-white">{b.rows[0].map((c, i) => <th key={i} className="text-left px-3 py-2 font-semibold break-words" dangerouslySetInnerHTML={H(c)} />)}</tr></thead>
        <tbody>{b.rows.slice(1).map((r, ri) => <tr key={ri} className="odd:bg-white even:bg-[#F1F5F9] border-t border-[#E2E8F0]">{r.map((c, ci) => <td key={ci} className="px-3 py-2 text-slate-300 align-top break-words" dangerouslySetInnerHTML={H(c)} />)}</tr>)}</tbody>
      </table>
    </div>
  );
  return null;
}

export default function Manuale() {
  const [doc, setDoc] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetch(`/manuale.json?v=${Date.now()}`).then((r) => r.json()).then(setDoc).catch(() => setDoc({ error: true }));
  }, []);

  useEffect(() => {
    if (!doc || !location.hash) return;
    const id = location.hash.slice(1);
    setActive(id);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [doc, location.hash]);

  const query = q.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!doc?.sections) return [];
    const base = doc.sections.filter((s) => strip(s.title).toLowerCase() !== "indice");
    if (!query) return base;
    return base.map((s) => {
      const titleHit = strip(s.title).toLowerCase().includes(query);
      const blocks = s.blocks.filter((b) => b.type.startsWith("h") || blockText(b).toLowerCase().includes(query));
      const hits = blocks.filter((b) => !b.type.startsWith("h")).length;
      return titleHit || hits ? { ...s, blocks: titleHit && !hits ? s.blocks : blocks, hits } : null;
    }).filter(Boolean);
  }, [doc, query]);

  const total = sections.reduce((a, s) => a + (s.hits || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="manual-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100 flex items-center gap-3"><BookOpen className="h-8 w-8 text-[#10B981]" /> Manuale</h1>
          <p className="text-slate-400 text-sm mt-1">Guida completa all'uso e alla configurazione · {doc?.version ? <span data-testid="manual-version">versione {doc.version} del {doc.date}</span> : "…"}</p>
        </div>
        <a href="/manuale-nouvelle-whatsapp.pdf" target="_blank" rel="noreferrer" data-testid="manual-pdf-link"
           className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] text-sm font-semibold transition-colors self-start">
          <Download className="h-4 w-4" /> Scarica PDF
        </a>
      </div>

      <div className="relative">
        <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel manuale (es. webhook, carosello, costo, token)…"
               className="bg-[#111827] border-[#1F2937] pl-9 pr-9 h-11" data-testid="manual-search-input" />
        {q && <button type="button" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200" data-testid="manual-search-clear"><X className="h-4 w-4" /></button>}
      </div>
      {query && <p className="text-xs text-slate-500" data-testid="manual-search-results">{total} risultat{total === 1 ? "o" : "i"} in {sections.length} capitol{sections.length === 1 ? "o" : "i"}</p>}

      {!doc ? <p className="text-slate-500 text-sm">Caricamento…</p> : doc.error ? <p className="text-[#EF4444] text-sm">Manuale non disponibile: verrà rigenerato al prossimo avvio.</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 min-w-0">
          <nav className="lg:sticky lg:top-6 self-start space-y-1 max-h-[70vh] overflow-y-auto pr-1 min-w-0" data-testid="manual-toc">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} onClick={() => setActive(s.id)} data-testid={`manual-toc-${s.id}`}
                 className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${active === s.id ? "bg-[#10B981]/15 text-[#10B981]" : "text-slate-400 hover:bg-[#111827] hover:text-slate-200"}`}>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span className="flex-1" dangerouslySetInnerHTML={{ __html: toHtml(s.title) }} />
                {query && s.hits > 0 && <span className="text-[10px] bg-[#F59E0B]/20 text-[#F59E0B] rounded-full px-1.5">{s.hits}</span>}
              </a>
            ))}
            {sections.length === 0 && <p className="text-xs text-slate-500 px-3">Nessun capitolo corrisponde alla ricerca.</p>}
          </nav>
          <div className="space-y-10 min-w-0">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-6 bg-[#111827]/60 border border-[#1F2937] rounded-2xl p-5 md:p-6 min-w-0 overflow-hidden" data-testid={`manual-section-${s.id}`}>
                <h2 className="font-display text-xl md:text-2xl font-bold text-slate-100 mb-3" dangerouslySetInnerHTML={{ __html: highlight(toHtml(s.title), query) }} />
                {s.blocks.map((b, i) => <Block key={i} b={b} q={query} />)}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
